import './style/editCostsDialog.scss';
import {AwesomeIcon, ChangeFunction, Loader, NumberString, voidFunction} from '@dvrd/dvr-controls';
import IDate, {DateType} from '@dvrd/idate';
import classNames from 'classnames';
import React from 'react';
import {useTranslation} from 'react-i18next';
import ProductCostsV2, {ProductCostsV2Fields} from '../../../models/productCostsV2Model';
import {DUTCH_DATE_FORMAT} from '../../../util/constants';
import {LVBSize} from '../../../util/enums';
import {ProductCostsFormErrors, ProductCostsSection} from '../../../util/types';
import BolProduct from '../../../models/bolProductModel';
import {truncateProductName} from '../../../util/productsUtil';
import {formatCurrency, formatLVBSize} from '../../../util/utils';
import {
    BluemateBadge,
    BluemateCollapsibleSection,
    BluemateDialog,
    BluemateInput,
    BluemateListCard,
    BluemateNumberInput,
    BluemateSegmentedToggle,
    BluemateTextarea,
    BluemateTooltip
} from '../../controls';
import {EditMode} from '../../../util/interfaces.ts';
import {SegmentedToggleOption} from '../../controls/button/bluemateSegmentedToggle.tsx';

interface Props {
    onClose: VoidFunction;
    onChangeForm: (key: keyof ProductCostsV2Fields, numeric?: boolean) => ChangeFunction<string | NumberString>;
    onChangeStartDate: (value: IDate) => void;
    onToggleAdvanced: (open: boolean) => void;
    onToggleSection: (section: ProductCostsSection) => (open: boolean) => void;
    onSelectRecord: (record: ProductCostsV2) => VoidFunction;
    onAddRecord: VoidFunction;
    onStopEditing: VoidFunction;
    onDeleteRecord: (record: ProductCostsV2) => (evt: React.MouseEvent) => void;
    onChangePeriodValue: (value: 'forever' | 'today' | 'fromDate') => void;
    onSubmit: VoidFunction;
    active: boolean;
    product: BolProduct | null;
    records: Array<ProductCostsV2>;
    periodEnds: Map<string, string | null>;
    loadingRecords: boolean;
    form: ProductCostsV2;
    lvbSize: LVBSize | null;
    advancedOpen: boolean;
    openSections: Set<ProductCostsSection>;
    errors: ProductCostsFormErrors;
    saving: boolean;
    deletingId: string | null;
    dataMode: EditMode;
    periodValue: 'forever' | 'today' | 'fromDate';
}

const euroOrnament = {element: <span className='euro-ornament'>&euro;</span>};

export default function EditCostsDialog(props: Props) {
    const {
        onClose,
        onChangeForm,
        onChangeStartDate,
        onToggleAdvanced,
        onToggleSection,
        onSelectRecord,
        onAddRecord,
        onStopEditing,
        onDeleteRecord,
        onChangePeriodValue,
        onSubmit,
        active,
        product,
        records,
        periodEnds,
        loadingRecords,
        form,
        lvbSize,
        advancedOpen,
        openSections,
        errors,
        saving,
        deletingId,
        dataMode,
        periodValue
    } = props;
    const {t} = useTranslation();
    const periodSegmentOptions: Array<SegmentedToggleOption<string>> = [
        {label: t('products.editCosts.always', 'Altijd'), value: 'forever'},
        {label: t('products.editCosts.fromToday', 'Vanaf vandaag'), value: 'today'},
        {label: t('products.editCosts.from', 'Vanaf') + '...', value: 'fromDate'}
    ];

    function renderLabel(text: string, hint: string) {
        return (
            <span className='label-with-info'>
                {text}
                <BluemateTooltip text={hint}>
                    <span className='info-icon-wrap'>
                        <AwesomeIcon name='info-circle' className='info-icon' />
                    </span>
                </BluemateTooltip>
            </span>
        );
    }

    function formatDate(costs: ProductCostsV2): string {
        if (costs.getFlex<boolean>('forever_selected')) return t('products.editCosts.always', 'Altijd');
        return costs.startDateAsDate.format(DUTCH_DATE_FORMAT);
    }

    function formatEndDate(costs: ProductCostsV2): string {
        if (costs.getFlex<boolean>('forever_selected')) return t('products.editCosts.always', 'Altijd');
        const end = periodEnds.get(costs.id) ?? null;
        if (end === null) return t('products.editCosts.untilNow', 'nu');
        return new IDate(end, {dateType: DateType.DATE_ONLY}).format(DUTCH_DATE_FORMAT);
    }

    function renderCostPrice() {
        return (
            <BluemateNumberInput
                onChange={onChangeForm('cost_price')}
                value={form.costPrice ?? 0}
                label={renderLabel(
                    t('products.editCosts.costPrice', 'Kostprijs per stuk'),
                    t(
                        'products.editCosts.costPriceHint',
                        'De inkoopprijs van één stuk. Bluemate trekt dit bedrag per verkocht stuk van je omzet af om de winst te berekenen.'
                    )
                )}
                className='cost-price-field'
                ornaments={euroOrnament}
                inputProps={{inputMode: 'decimal', type: 'text'}}
                size='small'
                shape='rounded'
                uniformFont
                error={errors.cost_price}
                disabled={saving}
                autoFocus
                autoSelect
                asCurrency
            />
        );
    }

    function renderDateInput() {
        if (periodValue != 'fromDate') return null;
        return (
            <BluemateInput
                label={t('products.editCosts.pickFromDate', 'Kies vanaf datum')}
                onChange={voidFunction}
                value={form.startDate}
                date={{onChange: onChangeStartDate, format: DUTCH_DATE_FORMAT}}
                placeholder={t('products.editCosts.datePlaceholder', 'dd-mm-jjjj')}
                size='small'
                shape='rounded'
                uniformFont
                fullWidth
            />
        );
    }

    function renderEditingNote() {
        if (dataMode === EditMode.NEW) return null;
        return (
            <BluemateBadge
                label={
                    <>
                        {t('products.editCosts.editingRecord', 'Je bewerkt de kosten die gelden vanaf {{date}}', {
                            date: formatDate(form)
                        })}
                        <BluemateTooltip text={t('products.editCosts.stopEditing', 'Stoppen met bewerken')}>
                            <AwesomeIcon name='xmark' className='btn-stop-editing' onClick={onStopEditing} />
                        </BluemateTooltip>
                    </>
                }
                color='orange'
                size='big'
                outline
                className='editing-note'
            />
        );
    }

    function renderSection(section: ProductCostsSection, title: string, subtitle: string, children: React.ReactNode) {
        return (
            <BluemateCollapsibleSection
                title={title}
                subtitle={subtitle}
                open={openSections.has(section)}
                onToggle={onToggleSection(section)}
                size='big'
                className='costs-section'
            >
                <div className='section-grid'>{children}</div>
            </BluemateCollapsibleSection>
        );
    }

    function renderAdvanced() {
        if (!form) return null;
        return (
            <BluemateCollapsibleSection
                title={t('products.editCosts.advanced', 'Geavanceerde opties')}
                open={advancedOpen}
                onToggle={onToggleAdvanced}
                size='big'
                className='advanced-section'
            >
                <div className='advanced-sections'>{renderSections()}</div>
            </BluemateCollapsibleSection>
        );
    }

    function renderSections() {
        if (!form) return null;
        return (
            <>
                {renderQuantitySection()}
                {renderFulfilmentSection()}
                {renderReturnsSection()}
                {renderMiscSection()}
            </>
        );
    }

    function renderQuantitySection() {
        return renderSection(
            'period',
            t('products.editCosts.sectionQuantity', 'Aantal'),
            t('products.editCosts.sectionQuantityHint', 'Vanaf welk aantal stuks deze kosten gelden'),
            <>
                <BluemateNumberInput
                    onChange={onChangeForm('min_quantity')}
                    value={form.minQuantity}
                    label={renderLabel(
                        t('products.editCosts.minQuantity', 'Vanaf aantal stuks'),
                        t(
                            'products.editCosts.minQuantityHint',
                            'Vanaf hoeveel stuks in één bestelregel deze kosten gelden. Laat 1 staan tenzij je met staffels werkt. Bij meerdere staffels gebruikt Bluemate de hoogste die past.'
                        )
                    )}
                    inputProps={{inputMode: 'numeric', maxLength: 4, type: 'text'}}
                    size='small'
                    shape='rounded'
                    uniformFont
                    autoSelect
                    wholeNumbers
                />
            </>
        );
    }

    function renderLabelledDivider(label: string) {
        return (
            <div className='labelled-divider section-col-2'>
                <span>{label}</span>
            </div>
        );
    }

    function renderFulfilmentSection() {
        return renderSection(
            'fulfilment',
            t('products.editCosts.sectionFulfilment', 'Fulfilmentkosten'),
            t('products.editCosts.sectionFulfilmentHint', 'LVB-formaat en de kosten van je eigen verzending'),
            <>
                {renderLabelledDivider(t('products.editCosts.fulfilmentBol', 'Fulfilment door bol (LVB)'))}
                <div className='lvb-size-value section-col-2'>
                    {renderLabel(
                        t('products.editCosts.lvbSize', 'LVB formaat'),
                        t(
                            'products.editCosts.lvbSizeHint',
                            'Het bol-formaat dat de LVB-fulfilmentkosten bepaalt. Bluemate bepaalt dit automatisch op basis van de afmetingen van het product bij bol.'
                        )
                    )}
                    <span className='lvb-value'>
                        {lvbSize ? formatLVBSize(lvbSize) : t('products.editCosts.lvbUnknown', 'Onbekend')}
                    </span>
                </div>
                {renderLabelledDivider(t('products.editCosts.fulfilmentOwn', 'Eigen verzending'))}
                <BluemateNumberInput
                    onChange={onChangeForm('pick_costs')}
                    value={form.pickCosts ?? ''}
                    label={renderLabel(
                        t('products.editCosts.pickCosts', 'Pick & pack kosten (eigen verzending)'),
                        t(
                            'products.editCosts.pickCostsHint',
                            'Wat het verzendklaar maken van één bestelling je kost. Telt alleen mee bij eigen verzending. Bij LVB rekent Bluemate met de tarieven van bol.'
                        )
                    )}
                    ornaments={euroOrnament}
                    inputProps={{inputMode: 'decimal', type: 'text'}}
                    size='small'
                    shape='rounded'
                    uniformFont
                    error={errors.pick_costs}
                    autoSelect
                    asCurrency
                />
                <BluemateNumberInput
                    onChange={onChangeForm('send_costs')}
                    value={form.sendCosts ?? ''}
                    label={renderLabel(
                        t('products.editCosts.sendCosts', 'Verzendkosten (eigen verzending)'),
                        t(
                            'products.editCosts.sendCostsHint',
                            'De vervoerderskosten per bestelling bij eigen verzending, bijvoorbeeld het pakketlabel. Telt niet mee bij LVB.'
                        )
                    )}
                    ornaments={euroOrnament}
                    inputProps={{inputMode: 'decimal', type: 'text'}}
                    size='small'
                    shape='rounded'
                    uniformFont
                    error={errors.send_costs}
                    autoSelect
                    asCurrency
                />
            </>
        );
    }

    function renderReturnsSection() {
        return renderSection(
            'returns',
            t('products.editCosts.sectionReturns', 'Retourkosten'),
            t('products.editCosts.sectionReturnsHint', 'Wat één retour je gemiddeld kost'),
            <>
                <BluemateNumberInput
                    onChange={onChangeForm('misc_return_costs')}
                    value={form.miscReturnCosts ?? ''}
                    label={renderLabel(
                        t('products.editCosts.returnCosts', 'Retourkosten'),
                        t(
                            'products.editCosts.returnCostsHint',
                            'Wat één retour je gemiddeld kost, bijvoorbeeld het retourlabel, controleren en opnieuw verpakken. Bluemate trekt dit af zodra een bestelling geretourneerd wordt.'
                        )
                    )}
                    ornaments={euroOrnament}
                    inputProps={{inputMode: 'decimal', type: 'text'}}
                    size='small'
                    shape='rounded'
                    className='section-col-2'
                    uniformFont
                    asCurrency
                    error={errors.misc_return_costs}
                    autoSelect
                />
                <BluemateTextarea
                    onChange={onChangeForm('misc_return_description', false)}
                    value={form.miscReturnDescription ?? ''}
                    label={renderLabel(
                        t('products.editCosts.returnCostsDesc', 'Omschrijving retourkosten'),
                        t(
                            'products.editCosts.returnCostsDescHint',
                            'Notitie voor jezelf waar deze retourkosten vandaan komen. Heeft geen invloed op de berekening.'
                        )
                    )}
                    placeholder={t('products.editCosts.descriptionPlaceholder', 'Omschrijving voor jezelf')}
                    rows={3}
                    size='small'
                    className='section-col-2'
                    uniformFont
                    fullWidth
                />
            </>
        );
    }

    function renderMiscSection() {
        return renderSection(
            'misc',
            t('products.editCosts.sectionMisc', 'Overige kosten'),
            t('products.editCosts.sectionMiscHint', 'Kosten per stuk die nergens anders staan'),
            <>
                <BluemateNumberInput
                    onChange={onChangeForm('misc_costs')}
                    value={form.miscCosts ?? ''}
                    label={renderLabel(
                        t('products.editCosts.miscCosts', 'Overige kosten'),
                        t(
                            'products.editCosts.miscCostsHint',
                            'Overige kosten per stuk die nog nergens anders staan, bijvoorbeeld verpakking of licentie. Tellen mee in de winstberekening.'
                        )
                    )}
                    ornaments={euroOrnament}
                    inputProps={{inputMode: 'decimal', type: 'text'}}
                    size='small'
                    shape='rounded'
                    className='section-col-2'
                    uniformFont
                    asCurrency
                    error={errors.misc_costs}
                    autoSelect
                />
                <BluemateTextarea
                    onChange={onChangeForm('misc_costs_description', false)}
                    value={form.miscCostsDescription ?? ''}
                    label={renderLabel(
                        t('products.editCosts.miscCostsDesc', 'Omschrijving overige kosten'),
                        t(
                            'products.editCosts.miscCostsDescHint',
                            'Notitie voor jezelf waar deze overige kosten vandaan komen. Heeft geen invloed op de berekening.'
                        )
                    )}
                    placeholder={t('products.editCosts.descriptionPlaceholder', 'Omschrijving voor jezelf')}
                    rows={3}
                    size='small'
                    className='section-col-2'
                    uniformFont
                    fullWidth
                />
            </>
        );
    }

    function renderHistory() {
        return (
            <BluemateListCard
                title={renderLabel(
                    t('products.editCosts.history', 'Geschiedenis'),
                    t(
                        'products.editCosts.historyHint',
                        'Elke wijziging vanaf een nieuwe datum komt hier als aparte regel te staan. Klik op het potlood om die periode te bewerken. Oudere bestellingen blijven met hun eigen kosten gerekend.'
                    )
                )}
                count={records.length || undefined}
                headerActions={renderAddRecordButton()}
                scrollX
                className='history-section'
            >
                <div className='default-table-row list-row head'>
                    <label>{t('products.editCosts.from', 'Vanaf')}</label>
                    <label>{t('products.editCosts.until', 'Tot')}</label>
                    <label>{t('products.editCosts.costPriceShort', 'Totale kosten')}</label>
                    <label className='col-quantity'>{t('products.editCosts.minQuantityShort', 'Vanaf aantal')}</label>
                    <label className='col-lvb'>{t('products.editCosts.lvbSize', 'LVB formaat')}</label>
                    <div />
                </div>
                {records.length ? (
                    records.map(renderHistoryRow)
                ) : (
                    <div className='default-table-row empty'>
                        <label className='empty-label'>
                            {t('products.editCosts.historyEmpty', 'Nog geen productkosten ingevuld.')}
                        </label>
                    </div>
                )}
            </BluemateListCard>
        );
    }

    function renderAddRecordButton() {
        if (dataMode === EditMode.NEW) return null;
        return (
            <BluemateTooltip text={t('products.editCosts.addNewRecord', 'Nieuwe kosten toevoegen')}>
                <button type='button' className='btn-add-record' onClick={onAddRecord}>
                    <AwesomeIcon name='plus' />
                </button>
            </BluemateTooltip>
        );
    }

    function renderHistoryRow(record: ProductCostsV2) {
        const selected = dataMode === EditMode.EDIT && form.id === record.id;
        return (
            <div
                key={record.id}
                className={classNames(
                    'default-table-row list-row',
                    selected && 'selected',
                    deletingId === record.id && 'deleting',
                    saving && 'busy'
                )}
            >
                <label>{formatDate(record)}</label>
                <label>{formatEndDate(record)}</label>
                <label>{record.costsTotal > 0 ? formatCurrency(record.costsTotal) : '-'}</label>
                <label className='col-quantity'>{record.minQuantity ?? 1}</label>
                <label className='col-lvb'>{formatLVBSize(record.lvbSize || null)}</label>
                <div className='row-actions'>
                    <AwesomeIcon name='pen' className='btn-edit' onClick={onSelectRecord(record)} />
                    <AwesomeIcon name='trash-alt' className='btn-delete' onClick={onDeleteRecord(record)} />
                </div>
            </div>
        );
    }

    return (
        <BluemateDialog
            active={active}
            onClose={onClose}
            title={t('products.editCosts.titleFor', 'Kosten aanpassen voor {{name}}', {
                name: truncateProductName(product?.title ?? product?.ean ?? '')
            })}
            subtitle={product ? `EAN: ${product.ean}` : undefined}
            onConfirm={onSubmit}
            confirmLabel={
                dataMode === EditMode.EDIT
                    ? t('products.editCosts.overwrite', 'Overschrijven')
                    : t('products.editCosts.add', 'Toevoegen')
            }
            cancelLabel={t('products.editCosts.cancel', 'Annuleren')}
            cancelVariant='tertiary'
            confirmDisabled={!form || loadingRecords}
            loading={saving}
            className='products-edit-costs-dialog'
            width='40rem'
        >
            <Loader active={loadingRecords} />
            {!!form && (
                <div className='costs-form'>
                    <div className='cost-price-row'>
                        {renderCostPrice()}
                        <BluemateSegmentedToggle
                            options={periodSegmentOptions}
                            value={periodValue}
                            onChange={onChangePeriodValue}
                            className='period-toggle'
                            size='small'
                        />
                        {renderDateInput()}
                    </div>
                    {renderEditingNote()}
                    {renderAdvanced()}
                    {renderHistory()}
                </div>
            )}
        </BluemateDialog>
    );
}
